import { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../front-end-global-components/components/Header/Header";
import {
  clearABdmConnectDetails,
  getScannedClinicDetails
} from "../../redux/abdm/actions";
import "./ScanClinic.css";

/**
 * Extract clinic identifiers from a scanned QR payload. Supports both a JSON
 * payload (e.g. {"hipId":"IN2410000975","code":"IN2410000975"}) and a URL
 * carrying "hip-id" / "counter-id" query params. Returns null for any payload
 * that doesn't carry a hipId so unrelated QR codes are ignored.
 * @param {string} decodedText
 * @returns {{ hipId: string, hipCode: string | null } | null}
 */
function parseClinicQr(decodedText) {
  // JSON payload, e.g. {"hipId":"IN2410000975","code":"IN2410000975"}
  try {
    const data = JSON.parse(decodedText);
    if (data && typeof data === "object" && data.hipId) {
      return { hipId: data.hipId, hipCode: data.code ?? null };
    }
  } catch (_) {}

  // URL payload, e.g. https://.../?hipId=...&counter-id=...
  try {
    const params = new URL(decodedText).searchParams;
    const hipId = params.get("hipId") ? params.get("hipId") : params.get("hip-id");
    if (hipId) {
      return { hipId, hipCode: params.get("counter-id") };
    }
  } catch (_) {}

  return null;
}

function ScanClinic(props) {
  const navigate = useNavigate();
  const hasScannedRef = useRef(false);
  const scannerRef = useRef(null);
  // Capture the latest props.abdm.loading in a ref so the onScanSuccess
  // callback (closed over at start time) always reads the current value.
  const abdmLoadingRef = useRef(props.abdm.loading);

  useEffect(() => {
    abdmLoadingRef.current = props.abdm.loading;
  }, [props.abdm.loading]);

  useEffect(() => {
    clearABdmConnectDetails();

    const scanner = new Html5Qrcode("qr-reader", {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false
    });
    scannerRef.current = scanner;
    let isStopped = false;

    function stopScanner() {
      if (isStopped) return;
      isStopped = true;
      scanner
        .stop()
        .then(() => scanner.clear())
        .catch(() => {});
    }

    function onScanSuccess(decodedText) {
      if (hasScannedRef.current || abdmLoadingRef.current !== false) return;

      const clinic = parseClinicQr(decodedText);
      // Ignore unrelated QR codes and keep scanning until a clinic QR is read.
      if (!clinic) return;

      hasScannedRef.current = true;
      stopScanner();

      getScannedClinicDetails(clinic.hipId, clinic.hipCode, navigate);
    }

    // Per-frame "not found" — fires on every non-QR frame, intentionally ignored.
    function onScanFailure() {}

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10 },
        onScanSuccess,
        onScanFailure
      )
      .catch(() => {
        // Camera permission denied or not available — nothing to do; the user
        // will see an empty container and can navigate back.
      });

    return () => {
      // Ensure camera is stopped, then clear the DOM element on unmount.
      const stopped = isStopped
        ? Promise.resolve()
        : scanner.stop().catch(() => {});
      isStopped = true;
      stopped.then(() => {
        try {
          scanner.clear();
        } catch (_) {}
      });
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div data-cy="scan-screen" className="inherit-parent-height">
      <Header
        title="Scan your clinic QR code"
        backButtonOnClick={() => {
          navigate("/");
        }}
        hideSupport
      />
      <div className="max-width-588px remaining-body-height overflow-hidden">
        <div id="qr-reader" className="inherit-parent-height" />
      </div>
      {props.abdm.loading === true && (
        <div className="position-absolute-center-self inherit-parent-height inherit-parent-width dimmed-overlay font-size-medium flex-place-children-page-center ">
          <div className="background-color-white padding-left-medium padding-right-medium padding-top-default padding-bottom-default border-radius-default ">
            <div className="loader" />
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  abdm: state.abdm,
  app: state.app
});

export default connect(mapStateToProps)(ScanClinic);
